import {
  type FieldErrors,
  type ItemsErrors,
  composeValidators,
  requiredText,
  runFieldValidation,
  validateItems,
} from "./validation";

/**
 * CERMIN dari backend: src/dos/create/request.ts (DOS_LIMITS). Backend adalah
 * sumber kebenaran (400 bila dilanggar); batas ini hanya untuk validasi klien.
 * Kalau batas di backend berubah, ubah juga di sini.
 */
export const DOS_LIMITS = {
  MIN_ITEMS: 1,
  MAX_ITEMS: 100,
  QTY_MAX: 1_000_000,
  DESCRIPTION_MAX: 1000,
  REMARK_MAX: 500,
  UNIT_MAX: 50,
  PO_NUMBER_MAX: 100,
  SIGNER_NAME_MAX: 100,
} as const;

export interface DosFormFields {
  customer_id_ref: number | null;
  sales_id: number | null;
  po_number: string;
  delivery_date: string;
  due_date: string;
  checked_by: string;
  received_by: string;
}

export interface DosFormItem {
  description: string;
  qty: number;
  unit: string;
  remark: string;
  product_id?: number | null;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** YYYY-MM-DD dan tanggalnya ada (dicek dengan memecah string, tanpa new Date). */
export const isValidDateOnly = (value: string) => {
  const match = DATE_ONLY.exec(value ?? "");
  if (!match) return false;

  const [year, month, day] = match.slice(1).map(Number);
  if (month < 1 || month > 12 || day < 1) return false;

  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

// Sama dengan backend: trim().length >= min dan length <= max.
const textLength = (label: string, min: number, max: number) => (value: unknown) => {
  const text = typeof value === "string" ? value : "";
  return text.trim().length >= min && text.length <= max
    ? undefined
    : `${label} must be ${min}-${max} characters`;
};

const dateOnly = (label: string) => (value: unknown) =>
  isValidDateOnly(typeof value === "string" ? value : "")
    ? undefined
    : `${label} must be a valid date`;

const wholeNumber = (label: string, max: number) => (value: unknown) =>
  typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= max
    ? undefined
    : `${label} must be a whole number between 1 and ${max.toLocaleString("en-US")}`;

export interface DosValidationResult {
  fieldErrors: FieldErrors;
  itemsErrors: ItemsErrors;
  itemsGeneralError?: string;
  hasError: boolean;
}

export const validateDosForm = (
  values: DosFormFields,
  items: DosFormItem[]
): DosValidationResult => {
  const fieldErrors = runFieldValidation<DosFormFields>(values, {
    customer_id_ref: requiredText("Customer is required"),
    sales_id: composeValidators(requiredText("Sales is required"), (value) =>
      value ? undefined : "Sales is required"
    ),
    po_number: textLength("PO Number", 1, DOS_LIMITS.PO_NUMBER_MAX),
    delivery_date: dateOnly("Delivery Date"),
    due_date: dateOnly("Due Date"),
    checked_by: textLength("Checked By", 1, DOS_LIMITS.SIGNER_NAME_MAX),
    received_by: textLength("Received By", 1, DOS_LIMITS.SIGNER_NAME_MAX),
  });

  const { itemsErrors, hasError: itemsHasError, generalError } = validateItems<DosFormItem>(
    items,
    {
      description: textLength("Description", 1, DOS_LIMITS.DESCRIPTION_MAX),
      qty: wholeNumber("Quantity", DOS_LIMITS.QTY_MAX),
      unit: textLength("Unit", 1, DOS_LIMITS.UNIT_MAX),
      remark: textLength("Remark", 0, DOS_LIMITS.REMARK_MAX),
    },
    `Items must be ${DOS_LIMITS.MIN_ITEMS}-${DOS_LIMITS.MAX_ITEMS} rows`
  );

  const tooManyItems = items.length > DOS_LIMITS.MAX_ITEMS;
  const itemsGeneralError = tooManyItems
    ? `Items must be ${DOS_LIMITS.MIN_ITEMS}-${DOS_LIMITS.MAX_ITEMS} rows`
    : generalError;

  return {
    fieldErrors,
    itemsErrors,
    itemsGeneralError,
    hasError: Object.keys(fieldErrors).length > 0 || itemsHasError || tooManyItems,
  };
};
